// /someday — the shelf, with a door.
//
// A task sent to Someday leaves every list and every count on purpose. The
// route then redirected to Today (the 2026-08 de-nav), so the only ways back
// were search and the assistant: a one-way door (walkthrough, 2026-09-20).
// This page is the way back. It lists what was set aside, and offers the
// same verbs the review offers — today, tomorrow, this week, drop — through
// the same handlers, so a verdict here cannot diverge from one given
// anywhere else. Nothing here is counted or nagged. What was let go ("not
// ever", letGo.ts) is kept but never listed here.
import { useMemo } from 'react'
import { usePendingDelete } from '@/hooks/usePendingDelete'
import { UndoToast } from '@/components/undo/UndoToast'
import { LoadFailedNotice } from '@/components/common/LoadFailedNotice'
import { useSupabaseTasks } from '@/hooks/useSupabaseTasks'
import { useGatedTaskActions } from '@/hooks/useGatedTaskActions'
import { useDomain } from '@/hooks/useDomain'
import { filterTasksForLayers } from '@/lib/today/domainFilter'
import { MastheadCard } from '@/components/layout/MastheadCard'
import { PAGE_COLUMN } from '@/components/layout/pageLayout'
import { TriageRow, applyTriageVerdict, type Verdict } from '@/components/schedule/TriageRow'
import type { Task } from '@/types/task'
import { isLetGo } from '@/lib/planning/letGo'

export function SomedayPage() {
  const { tasks, loading, error, refetch, toggleTask, updateTask, deleteTask, pushTask, updateTasksBulk } = useSupabaseTasks()
  const { layers, all: showAllDomains } = useDomain()
  const gated = useGatedTaskActions({ updateTask, pushTask, updateTasksBulk }, (id) => tasks.find((t) => t.id === id))

  // Delete is one tap from the row, so it waits out an Undo window first.
  const pendingDelete = usePendingDelete((id) => { void deleteTask(id) })
  const pendingIds = pendingDelete.pendingIds
  const setAside = useMemo(
    () => tasks.filter((t) => t.bucket === 'someday' && !t.completed && !isLetGo(t) && !pendingIds.includes(t.id)),
    [tasks, pendingIds],
  )
  const rows = useMemo(
    () => filterTasksForLayers(setAside, layers)
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()),
    [setAside, layers],
  )
  // Empty because of the domain filter, not because nothing was set aside.
  const hiddenByFilter = rows.length === 0 && setAside.length > 0
  // A failed read leaves the list empty; that is not "Nothing set aside."
  // (The hook's error is also set by failed writes, so only when nothing arrived.)
  const loadFailed = !loading && !!error && tasks.length === 0

  const viewedDate = useMemo(() => new Date(), [])
  const onVerdict = (t: Task, v: Verdict) => {
    void applyTriageVerdict(t, v, {
      viewedDate,
      onUpdateTask: (id, u) => gated.updateTask(id, u),
      onPushTask: (id, target) => gated.pushTask(id, target),
      onDeleteTask: (id) => pendingDelete.schedule([id], 'Deleted'),
    })
  }

  return (
    <div className="h-full overflow-auto">
      <div className={PAGE_COLUMN}>
        <MastheadCard
          variant="page"
          title="Someday"
          motif="history"
          subline="Set aside on purpose. Nothing here is counted or nagged — pull one back when its time comes."
        />
        <section aria-label="Someday" className="mt-4">
          {loading && rows.length === 0 ? (
            <p className="py-6 text-[15px] text-neutral-500">Loading…</p>
          ) : loadFailed ? (
            <LoadFailedNotice variant="inline" className="py-6 text-[15px] text-neutral-500" title="Someday didn’t load." onRetry={() => { void refetch() }} />
          ) : hiddenByFilter ? (
            <p className="py-6 text-[15px] text-neutral-500">
              Nothing set aside in the areas you're viewing.{' '}
              <button type="button" onClick={showAllDomains} className="font-medium text-primary-600 underline-offset-2 hover:underline">
                Show all domains
              </button>
            </p>
          ) : rows.length === 0 ? (
            <p className="py-6 text-[15px] text-neutral-500">Nothing set aside.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-neutral-200">
              {rows.map((t) => (
                <li key={t.id}>
                  <TriageRow
                    task={t}
                    lead="today"
                    offer={['today', 'tomorrow', 'week', 'deleted']}
                    canDelete
                    onVerdict={onVerdict}
                    onComplete={(task) => { void toggleTask(task.id) }}
                  />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      <UndoToast action={pendingDelete.action} onUndo={pendingDelete.undo} onDismiss={pendingDelete.dismiss} />
    </div>
  )
}
